import compact from 'lodash/compact';
import difference from 'lodash/difference';
import flat from 'lodash/flatten';
import groupBy from 'lodash/groupBy';
import upperFirst from 'lodash/upperFirst';
import type { CalendarSpec, Moment, unitOfTime } from 'moment';

import { translate } from '@front/i18n';

import {
  CHILDCARE_APPOINTMENT_STEP,
  EARLY_SURCHARGE_END_TIME,
  LATE_SURCHARGE_START_TIME,
  WELLNESS_APPOINTMENT_STEP,
} from '../constants/appointment';
import { DAY_MINOR_FORMAT, WEEKDAY_DAY_MONTH_FORMAT } from '../constants/date';
import { HOURLY_SURCHARGE, PRESTATIONS_BASED_SURCHARGE } from '../constants/prices';
import type {
  Appointment,
  BusinessHours,
  ChildcareHourSlot,
  MarkedDate,
  MarkedDates,
  WellnessHourSlot,
} from '../types/commons';
import type { Booking, ChildcareRecurringOrder, CleaningRecurringOrder } from '../types/customer';
import { isRecurrentRecurrence } from './cleaning';
import { FORMATS, formatDate, formatStandardDate } from './date';
import { formatPrice, formatPricePerHour } from './formatting';
import moment from './moment';
import { formatStandardTime, formatTime, parseTime } from './time';

const t = translate('appointment');

const getSurchargedTimeRanges = () => [
  {
    start: '00:00',
    end: EARLY_SURCHARGE_END_TIME,
  },
  {
    start: LATE_SURCHARGE_START_TIME,
    end: '24:00',
  },
];

type FormatRecurrentAppointmentParams = {
  appointment: Moment | string;
  recurrence: number;
  recurrenceNextAppointment?: string;
  weeklyI18nKey: string;
  biWeeklyI18nKey: string;
  every4WeeksI18nKey: string;
  severalI18nKey: string;
};

const formatRecurrentAppointment = ({
  appointment,
  recurrence,
  recurrenceNextAppointment,
  weeklyI18nKey,
  biWeeklyI18nKey,
  every4WeeksI18nKey,
  severalI18nKey,
}: FormatRecurrentAppointmentParams) => {
  const formattedAppointment = formatDate(appointment, FORMATS.DATETIME.WEEKDAY_DAY_MONTH_AT_TIME_SHORT);
  const day = recurrenceNextAppointment || appointment;
  const timeFormatted = formatTime(day);

  switch (recurrence) {
    case 1:
      return t(weeklyI18nKey, {
        first: formattedAppointment,
        day: formatDate(day, FORMATS.DATE.WEEKDAY),
        time: timeFormatted,
      });
    case 0.5:
      return t(biWeeklyI18nKey, {
        first: formattedAppointment,
        day: formatDate(day, FORMATS.DATE.WEEKDAY),
        time: timeFormatted,
      });
    case 0.25:
      return t(every4WeeksI18nKey, {
        first: formattedAppointment,
        day: formatDate(day, FORMATS.DATE.WEEKDAY),
        time: timeFormatted,
      });
    default:
      return t(severalI18nKey, {
        first: formattedAppointment,
        recurrence,
        time: timeFormatted,
      });
  }
};

type FormatAppointmentProps = Pick<
  FormatRecurrentAppointmentParams,
  'recurrenceNextAppointment' | 'weeklyI18nKey' | 'biWeeklyI18nKey' | 'every4WeeksI18nKey' | 'severalI18nKey'
> & {
  appointment: Moment | string | null;
  recurrence?: number | null;
};

const formatAppointment = ({
  appointment,
  recurrence,
  recurrenceNextAppointment,
  weeklyI18nKey,
  biWeeklyI18nKey,
  every4WeeksI18nKey,
  severalI18nKey,
}: FormatAppointmentProps) => {
  if (!appointment) return '-';

  if (recurrence)
    return formatRecurrentAppointment({
      appointment,
      recurrence,
      recurrenceNextAppointment,
      weeklyI18nKey,
      biWeeklyI18nKey,
      every4WeeksI18nKey,
      severalI18nKey,
    });

  const formattedAppointment = formatDate(appointment, FORMATS.DATETIME.WEEKDAY_DAY_MONTH_AT_TIME_SHORT);

  return upperFirst(formattedAppointment);
};

export const isPresent = (arr: Moment[], value: Moment) => arr.some((date) => date.isSame(value));

export const getDaysOfWeek = (start: Moment = moment(), daysToDisplay = 8) =>
  Array.from(Array(daysToDisplay).keys()).map((offset) => start.clone().add(offset, 'days'));

export const isDateOneWeekAfter = (date: Moment, start: Moment = moment()) =>
  date.isSameOrAfter(moment(start).add(7, 'days'), 'day');

export const getDateLabel = (date: Moment, showDayOnly = false) => {
  const baseConfig: CalendarSpec = {
    lastWeek: DAY_MINOR_FORMAT,
    lastDay: DAY_MINOR_FORMAT,
    sameDay: `[${t('.today')}]`,
    nextDay: `[${t('.tomorrow')}]`,
    nextWeek: DAY_MINOR_FORMAT,
    sameElse: DAY_MINOR_FORMAT,
  };

  const defaultConfig: CalendarSpec = baseConfig;

  const dayOnlyConfig: CalendarSpec = {
    ...baseConfig,
    lastWeek: DAY_MINOR_FORMAT,
    lastDay: DAY_MINOR_FORMAT,
    sameDay: DAY_MINOR_FORMAT,
    nextDay: DAY_MINOR_FORMAT,
  };

  const label = date.calendar(null, showDayOnly ? dayOnlyConfig : defaultConfig);

  return [upperFirst(label), date.format(WEEKDAY_DAY_MONTH_FORMAT)];
};

type GetDayTitleParams = { day: Moment; primary: Moment | null | undefined; start?: Moment; showDayOnly?: boolean };

export const getDayTitle = ({ day, primary, start, showDayOnly = false }: GetDayTitleParams) => {
  if (isDateOneWeekAfter(day, start)) {
    // If primary is after one week, we display its exact date instead of showing "Other date"
    return primary && isDateOneWeekAfter(primary, start)
      ? getDateLabel(primary)
      : [t('.another_date'), t('.see_calendar')];
  }
  return getDateLabel(day, showDayOnly);
};

export const getNextAvailabilityFromDate = (date?: string | null) => {
  if (!date) {
    const twoWeeksAfterToday = formatDate(moment().add(2, 'weeks'), FORMATS.DATE.DAY_MONTH_SHORT);

    return t('.available_after', { date: twoWeeksAfterToday });
  }

  const momentDate = moment(date);
  const [formattedDate, fullDateFormatted] = getDateLabel(momentDate);

  if (isDateOneWeekAfter(momentDate)) {
    return t('.available_from', { date: fullDateFormatted.toLowerCase() });
  }

  return t('.available_from', { date: formattedDate.toLowerCase() });
};

export const getAppointmentsCount = (day: Moment, { primary, secondary }: Appointment, start?: Moment) =>
  compact([primary, ...secondary]).filter((appointment) =>
    isDateOneWeekAfter(day, start) ? isDateOneWeekAfter(appointment, start) : appointment.isSame(day, 'day'),
  ).length;

type MomentsByDate = Record<string, Moment[]>;

export const getAppointmentsByDate = (appointments: Moment[]): MomentsByDate =>
  appointments.reduce((acc: MomentsByDate, appointment) => {
    const date = formatStandardDate(appointment);
    return {
      ...acc,
      [date]: [...(acc[date] || []), appointment],
    };
  }, {});

export const hasExpiredAppointment = (appointments: Moment[], minimumAppointment: Moment) =>
  appointments.some((date) => date.isBefore(minimumAppointment));

/**
 * @see https://github.com/moment/moment/issues/3762
 */
export const getDefaultAppointmentMaxDate = (date?: Moment) =>
  date ? moment(date).add(6, 'months').startOf('day') : moment().add(6, 'months').startOf('day');

export const getDefaultSpotTimeRange = (nightly: boolean) =>
  nightly
    ? {
        start: '18:30',
        end: '01:00',
      }
    : {
        start: '07:00',
        end: '23:59',
      };

// This utils is here to compose appointment recurrence with different values
// {
//   formattedRecurrence: 'chaque jeudi à 20h00',
//   nextRecurrence: 'Prochaine session le lun. 30 déc. à 18h00, puis chaque jeudi',
//   then: 'puis chaque jeudi à 20h00',
// }
export const composeFormattedAppointmentRecurrence = (
  date: Moment | string | null,
  recurrence?: number | null,
  recurrenceNextAppointment?: string,
) => {
  const formattedRecurrence = formatAppointment({
    appointment: date,
    recurrence,
    weeklyI18nKey: '.recurrence.weekly',
    biWeeklyI18nKey: '.recurrence.biweekly',
    every4WeeksI18nKey: '.recurrence.every_4_weeks',
    severalI18nKey: '.recurrence.several',
    recurrenceNextAppointment,
  });

  return {
    formattedRecurrence,
    nextRecurrence: formatAppointment({
      appointment: date,
      recurrence,
      weeklyI18nKey: '.recurrence_next.weekly',
      biWeeklyI18nKey: '.recurrence_next.biweekly',
      every4WeeksI18nKey: '.recurrence_next.every_4_weeks',
      severalI18nKey: '.recurrence_next.several',
      recurrenceNextAppointment,
    }),
    then:
      !isRecurrentRecurrence(recurrence) || !date
        ? ''
        : t('.recurrence.then', { then: t('.then'), formattedRecurrence }),
  };
};

export const formatPrimaryAppointment = (primary: Moment | string | null, recurrence?: number | null) =>
  formatAppointment({
    appointment: primary,
    recurrence,
    weeklyI18nKey: '.primary.weekly',
    biWeeklyI18nKey: '.primary.biweekly',
    every4WeeksI18nKey: '.primary.every_4_weeks',
    severalI18nKey: '.primary.several',
  });

export const formatPrimaryAppointmentCondensed = (primary: Moment | string | null, recurrence?: number | null) =>
  formatAppointment({
    appointment: primary,
    recurrence,
    weeklyI18nKey: '.primary_condensed.weekly',
    biWeeklyI18nKey: '.primary_condensed.biweekly',
    every4WeeksI18nKey: '.primary_condensed.every_4_weeks',
    severalI18nKey: '.primary_condensed.several',
  });

export const formatSecondaryAppointments = (datetimes: Moment[] | string[], recurrence?: number | null) => {
  if (!datetimes.length) return '-';

  const sortedDatetimes = datetimes.map((dt) => (moment.isMoment(dt) ? dt : moment(dt))).sort();
  // moment startOf() mutates the original object! -> clone
  const datesByDay = groupBy(sortedDatetimes, (item) => item.clone().startOf('day'));
  const dateFormat =
    recurrence && [0.25, 0.5, 1].includes(recurrence)
      ? FORMATS.DATE.WEEKDAY_PLURAL_AT
      : FORMATS.DATE.WEEKDAY_DAY_MONTH_SHORT_AT;

  return Object.values(datesByDay)
    .map((dates) => `${formatDate(dates[0], dateFormat)} ${dates.map((date) => formatTime(date)).join(', ')}`)
    .join(` ${t('base.or')} `);
};

export const formatPrimaryAppointmentWithDuration = (
  appointment?: string | Moment | null,
  duration?: number | null,
) => {
  if (!appointment || !duration) return '-';

  const startDate = moment(appointment);
  const endDate = moment(startDate).add(duration, 'minutes');
  const endsTomorrow = endDate.isAfter(startDate, 'day');

  const date = upperFirst(formatDate(startDate, FORMATS.DATE.WEEKDAY_DAY_MONTH_SHORT));
  const startTime = formatStandardTime(startDate);
  const endTime = formatStandardTime(endDate);
  const nextDay = endsTomorrow ? formatDate(endDate, FORMATS.DATE.WEEKDAY_SHORT) : undefined;

  return nextDay
    ? t('.primary.with_duration_overflow', {
        date,
        startTime,
        endTime,
        nextDay,
      })
    : t('.primary.with_duration', { date, startTime, endTime });
};

export const roundUpToStep = (minutes: number, step: number) => Math.ceil(minutes / step) * step;

export const roundDownToStep = (minutes: number, step: number) => Math.floor(minutes / step) * step;

const getSlotsBetween = (min: Moment, max: Moment, { step = WELLNESS_APPOINTMENT_STEP, includeMax = true } = {}) => {
  // Example for wellness with step 30 minutes:
  // If min is 17:00 => min slot will be 17:00
  // If min is between 17:01 and 17:30 => min slot will be 17:30
  // If min is between 17:31 and 18:00 => min slot will be 18:00
  const minSlot = min.clone().set({ minute: roundUpToStep(min.minute(), step), second: 0, millisecond: 0 });

  if (!minSlot.isSameOrBefore(max)) return [];

  const minMaxMinutesDifference = max.diff(minSlot, 'minute');
  const numberOfSlots = Math.floor(minMaxMinutesDifference / step) + (includeMax ? 1 : 0);

  const slots = Array.from({ length: numberOfSlots }, (_, i) =>
    formatStandardTime(minSlot.clone().add(step * i, 'minute')),
  );

  const maxSlot = formatStandardTime(max);
  return includeMax && !slots.includes(maxSlot) ? [...slots, maxSlot] : slots;
};
export const getChildcareSchedulesBetween = (min: Moment, max: Moment): ChildcareHourSlot[] =>
  getSlotsBetween(min, max, { step: CHILDCARE_APPOINTMENT_STEP }) as ChildcareHourSlot[];

type GetWellnessHoursBetweenParams = {
  min: Moment;
  max: Moment;
  options?: { includeMax: boolean };
};

export const getWellnessSlotsBetween = ({
  min,
  max,
  options = { includeMax: false },
}: GetWellnessHoursBetweenParams): WellnessHourSlot[] => getSlotsBetween(min, max, options) as WellnessHourSlot[];

export const getMinStartTime = (minValue: Moment, minimumAppointment?: Moment) =>
  minimumAppointment?.isSame(minValue, 'day') && minimumAppointment.isAfter(minValue) ? minimumAppointment : minValue;

export const getSurchargedSlots = (): WellnessHourSlot[] =>
  flat(
    getSurchargedTimeRanges().map(({ start, end }) =>
      getWellnessSlotsBetween({ min: parseTime(start), max: parseTime(end) }),
    ),
  );

export const getSurchargeText = (isCleaning: boolean) =>
  isCleaning
    ? `+${formatPricePerHour(HOURLY_SURCHARGE, { precision: 0 })}`
    : `+${formatPrice(PRESTATIONS_BASED_SURCHARGE, { precision: 0 })}`;

type GetMarkersForDayParams = {
  day?: Moment | null;
  weeklyRecurrence: number | null;
  additionalMarkers?: MarkedDates;
  markAllAs?: MarkedDate;
};

export const getMarkersForDay = ({
  day,
  weeklyRecurrence,
  additionalMarkers = {},
  markAllAs,
}: GetMarkersForDayParams): MarkedDates => {
  if (!day) return additionalMarkers;
  if (weeklyRecurrence === 1) {
    return {
      ...additionalMarkers,
      [formatStandardDate(day)]: markAllAs || { isPrimary: true },
      [formatStandardDate(day.clone().add(7, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(14, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(21, 'days'))]: markAllAs || { isSecondary: true },
    };
  }
  if (weeklyRecurrence === 0.5) {
    return {
      ...additionalMarkers,
      [formatStandardDate(day)]: markAllAs || { isPrimary: true },
      [formatStandardDate(day.clone().add(14, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(28, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(42, 'days'))]: markAllAs || { isSecondary: true },
    };
  }
  if (weeklyRecurrence === 0.25) {
    return {
      ...additionalMarkers,
      [formatStandardDate(day)]: markAllAs || { isPrimary: true },
      [formatStandardDate(day.clone().add(28, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(56, 'days'))]: markAllAs || { isSecondary: true },
      [formatStandardDate(day.clone().add(84, 'days'))]: markAllAs || { isSecondary: true },
    };
  }
  return {
    ...additionalMarkers,
    [formatStandardDate(day)]: markAllAs || { isPrimary: true },
  };
};

type GetMarkersFromBookingParams = {
  booking: Booking;
  newSelectedDate?: Moment | null;
  markAllAs?: MarkedDate;
};

export const getMarkersFromBooking = ({ booking, newSelectedDate, markAllAs }: GetMarkersFromBookingParams) => {
  const markers: MarkedDates = {};

  // Calculate the markers depending on the recurrence orders sent by the back
  const { appointment } = booking;
  const currentAppointment = moment(appointment);

  const currentAppointmentFormatted = formatStandardDate(currentAppointment);
  const recurrenceOrders = (booking as CleaningRecurringOrder | ChildcareRecurringOrder).recurrence_orders || [];

  // Set markers for all order dates except for the current modified occurrence
  recurrenceOrders
    .filter((recurrenceOrder) => !currentAppointment.isSame(moment(recurrenceOrder.appointment), 'day'))
    .forEach((recurrenceOrder) => {
      markers[formatStandardDate(recurrenceOrder.appointment)] = markAllAs || { isSecondary: true };
    });

  if (newSelectedDate) {
    markers[formatStandardDate(newSelectedDate)] = markAllAs || { isPrimary: true };
  } else {
    markers[currentAppointmentFormatted] = markAllAs || { isPrimary: true };
  }

  return markers;
};

type GetMarkersForDateRangeParams = {
  startDate?: Moment | null;
  endDate?: Moment | null;
};

export const getMarkersForDateRange = ({ startDate, endDate }: GetMarkersForDateRangeParams): MarkedDates => {
  if (!startDate) {
    return {};
  }
  if (!endDate) {
    return {
      [formatStandardDate(startDate)]: { isPrimary: true },
    };
  }
  if (startDate.isSame(endDate, 'day')) {
    return {
      [formatStandardDate(startDate)]: { isPrimary: true },
    };
  }
  if (startDate.isBefore(endDate, 'day')) {
    const daysDifference = endDate.diff(startDate, 'days') + 1;

    return Array.from({ length: daysDifference }, (_, index) => {
      const currentDate = moment(startDate).add(index, 'days');
      return {
        [formatStandardDate(currentDate)]: { isPrimary: true },
      };
    }).reduce((result, date) => ({ ...result, ...date }), {});
  }

  return {};
};

export const nextPrestationText = (weeklyRecurrence: number | null, isModification?: boolean) => {
  if (weeklyRecurrence) {
    return isModification
      ? t('.prestation_on_modified') // Several occurrences and modified
      : t('.first_prestation_on'); // Several occurrences and created
  }
  return t('.prestation_on');
};

type Slot = {
  text: string;
  isPrimary: boolean;
  label: string;
};
type DayWithSlot = {
  day: Moment;
  slots: Slot[];
};

export const getAppointmentsSortedByDay = (primary?: Moment | null, secondary: Moment[] = []) => {
  if (!primary) return [];

  return [primary, ...secondary]
    .sort((appointment1, appointment2) => appointment1.diff(appointment2))
    .reduce((acc: DayWithSlot[], appointment) => {
      const day = appointment.clone().startOf('day');

      const dayWithSlots = acc.find((value) => value.day.isSame(day)) || { day, slots: [] };
      dayWithSlots.slots = [
        ...dayWithSlots.slots,
        {
          text: formatStandardTime(appointment),
          isPrimary: appointment.isSame(primary),
          label: formatDate(appointment, FORMATS.DATETIME.WEEKDAY_DAY_MONTH_YEAR_AT_TIME),
        },
      ];

      return dayWithSlots.slots.length > 1 ? acc : [...acc, dayWithSlots];
    }, []);
};

type AvailableHoursParams = {
  business_hours: BusinessHours;
  selectedDate: Moment;
  minimumAppointment: Moment;
  closedTimeSlots: Moment[];
  selectedAppointments?: Moment[];
};

/**
 * selectedDate cannot be before minimumAppointment since the list of
 * selectable days start from the day with the minimum appointment.
 * @see {@link packages/web/src/screens/appointment-day/appointment-day.js}
 */
export const getOpenSlots = ({
  business_hours,
  selectedDate,
  minimumAppointment,
  closedTimeSlots,
  selectedAppointments,
}: AvailableHoursParams): WellnessHourSlot[] => {
  const { start, end } = business_hours;

  const [min, max] = selectedDate.isSame(minimumAppointment, 'day')
    ? [minimumAppointment, minimumAppointment.clone().set(end)]
    : [moment().set(start), moment().set(end)];

  const closedTimeSlotsToday = closedTimeSlots
    .filter((slot) => slot.isSame(selectedDate, 'day'))
    .map(formatStandardTime);

  const appointmentsSlotsToday = selectedAppointments
    ? selectedAppointments.filter((appointment) => appointment.isSame(selectedDate, 'day')).map(formatStandardTime)
    : [];

  // create a set to remove duplicates
  const todaySlots = new Set(
    // Remove closed time slots from calculated wellness hour slots and add slots from the appointment if any
    difference(getWellnessSlotsBetween({ min, max }), closedTimeSlotsToday).concat(appointmentsSlotsToday),
  );

  return Array.from(todaySlots).sort((a, b) => a.localeCompare(b)) as WellnessHourSlot[];
};

export type AppointmentInterval = { start: Moment; end: Moment };

export const getAppointmentIntervalsFromHourSlots = (
  hourSlots: WellnessHourSlot[] | ChildcareHourSlot[],
  step: number = WELLNESS_APPOINTMENT_STEP,
) => {
  if (hourSlots.length < 2) return [];

  const parsedHourSlots = hourSlots.map(parseTime);

  /**
   * Each hour slot means that time is available +{step} minutes.
   *
   * Simplified example with wellness (30 minutes steps) :
   * for ['07:00', '07:30', '10:00'], the intervals should be [
   *    { start: '07:00', end: '08:00' },
   *    { start: '10:00', end: '10:30' },
   * ]
   *
   * So this function does the following :
   *
   * ['07:00', '07:30', '10:00']
   *   .reduce() // [{ start: '07:00', end: '07:30' }, { start: '10:00', end: '10:00' }]
   *   .map()    // [{ start: '07:00', end: '08:00' }, { start: '10:00', end: '10:30' }]
   */
  return parsedHourSlots
    .reduce(
      (allIntervals: AppointmentInterval[], hourSlot) => {
        const allIntervalsExceptLast = allIntervals.slice(0, -1);
        const lastInterval = allIntervals[allIntervals.length - 1];

        /*
         * This condition checks whether the gap between the next hour slot and the last
         * interval's end is equal or smaller than the used step.
         * e.g. the last interval is { start: '10:00', end: '10:30' }
         * if the next hour slot is '11:00', it'll replace the last interval's end.
         * So the last interval would then be: { start: '10:00', end: '11:00' }
         * But if the gap between next hour slot is greater than the step, it will set the
         * next interval as { start: '11:00', end: '11:00' }
         */
        if (hourSlot.diff(lastInterval.end, 'minutes') <= step) {
          return [...allIntervalsExceptLast, { ...lastInterval, end: hourSlot }];
        }

        return [...allIntervals, { start: hourSlot, end: hourSlot }];
      },
      [{ start: parsedHourSlots[0], end: parsedHourSlots[1] }],
    )
    .map(({ start, end }) => ({
      start,
      end: end.clone().add(step, 'minutes'),
    }));
};

type GetDelayBetweenDatesParams = {
  firstDate: Moment | string;
  secondDate?: Moment | string;
  unit?: unitOfTime.Base;
};

export const getDelayBetweenDates = ({
  firstDate,
  secondDate = moment(),
  unit = 'hours',
}: GetDelayBetweenDatesParams) => {
  const momentFirstDate = moment.isMoment(firstDate) ? firstDate : moment(firstDate);
  const momentSecondDate = moment.isMoment(secondDate) ? secondDate : moment(secondDate);

  return Math.abs(momentSecondDate.diff(momentFirstDate, unit, true));
};
